import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { PORTAL_INFO } from "../../constants/app";
import "./InstallPWA.css";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
}

declare global {
  interface Window {
    __deferredInstallPromptEvent?: BeforeInstallPromptEvent | null;
  }

  interface Navigator {
    standalone?: boolean;
  }
}

const getDeferredPrompt = (): BeforeInstallPromptEvent | null => {
  return window.__deferredInstallPromptEvent || null;
};

const InstallPWA: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(getDeferredPrompt());
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  const iosBrowserName = isIOS && /CriOS/i.test(window.navigator.userAgent) ? "Chrome" : "Safari";

  useEffect(() => {
    const userAgent = window.navigator.userAgent;
    const isIOSDevice =
      /iPad|iPhone|iPod/.test(userAgent) ||
      (window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1);
    const isAndroidDevice = /Android/i.test(userAgent);
    const isStandaloneMode = window.navigator.standalone === true || window.matchMedia("(display-mode: standalone)").matches;

    setIsIOS(isIOSDevice);
    setIsAndroid(isAndroidDevice);
    setIsStandalone(isStandaloneMode);

    if (isStandaloneMode) {
      return;
    }

    const syncDeferredPrompt = () => {
      const installPrompt = getDeferredPrompt();
      setDeferredPrompt(installPrompt);

      if (installPrompt) {
        setShowModal(true);
      }
    };

    const handleBeforeInstallPrompt = (event: Event) => {
      const installEvent = event as BeforeInstallPromptEvent;
      installEvent.preventDefault();
      window.__deferredInstallPromptEvent = installEvent;
      syncDeferredPrompt();
      setShowModal(true);
    };

    const handleInstalled = () => {
      window.__deferredInstallPromptEvent = null;
      setDeferredPrompt(null);
      setShowModal(false);
      setIsStandalone(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("pwa-installable", syncDeferredPrompt);
    window.addEventListener("appinstalled", handleInstalled);
    syncDeferredPrompt();

    let fallbackTimer: number | undefined;

    if (isIOSDevice && !window.localStorage.getItem(PORTAL_INFO.IOS_DISMISS_KEY)) {
      fallbackTimer = window.setTimeout(() => {
        setShowModal(true);
      }, 2000);
    }

    if (isAndroidDevice) {
      fallbackTimer = window.setTimeout(() => {
        if (!getDeferredPrompt()) {
          setShowModal(true);
        }
      }, 2000);
    }

    return () => {
      if (fallbackTimer) {
        window.clearTimeout(fallbackTimer);
      }

      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("pwa-installable", syncDeferredPrompt);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      return;
    }

    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      window.__deferredInstallPromptEvent = null;
      setDeferredPrompt(null);
    }

    setShowModal(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);

    if (isIOS) {
      window.localStorage.setItem(PORTAL_INFO.IOS_DISMISS_KEY, "true");
    }
  };

  if (isStandalone || !showModal) {
    return null;
  }

  return createPortal(
    <div className="install-pwa-overlay" role="presentation">
      <div className="install-pwa-modal" role="dialog" aria-modal="true" aria-labelledby="install-pwa-title">
        <div className="install-pwa-header">
          <img src="/assets/icon/favicon.png" alt={PORTAL_INFO.NAME} className="install-pwa-logo" />
          <h2 id="install-pwa-title">Crear Acceso Directo</h2>
          <p>
            al Portal de Paciente {PORTAL_INFO.NAME}
            <br />
            para ingresar m&aacute;s r&aacute;pido desde tu dispositivo
          </p>
        </div>

        {isIOS ? (
          <div className="install-pwa-content">
            <ol>
              <li>Toc&aacute; Compartir en {iosBrowserName}.</li>
              <li>Eleg&iacute; Agregar a pantalla de inicio.</li>
              <li>Toc&aacute; Agregar para confirmar.</li>
            </ol>
            <small>En iPhone la instalaci&oacute;n se realiza manualmente porque iOS no ofrece el bot&oacute;n nativo.</small>
          </div>
        ) : isAndroid && !deferredPrompt ? (
          <div className="install-pwa-content">
            <ol>
              <li>Toc&aacute; el men&uacute; de tres puntos del navegador.</li>
              <li>Eleg&iacute; Instalar app o Agregar a pantalla principal.</li>
              <li>Confirm&aacute; la instalaci&oacute;n.</li>
            </ol>
          </div>
        ) : (
          <div className="install-pwa-content" />
        )}

        <div className="install-pwa-actions">
          {deferredPrompt && (
            <button type="button" className="install-pwa-button install-pwa-button-primary" onClick={handleInstall}>
              Instalar
            </button>
          )}
          <button type="button" className="install-pwa-button install-pwa-button-secondary" onClick={handleCloseModal}>
            {isIOS ? "Entendido" : "Ahora no"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default InstallPWA;
